<template>
  <v-card
    outlined
    data-testid="operation-card"
    :data-operation-id="operation.id"
    class="rounded-lg pa-2  operation-card "
    :class="{ 'operation-card--newest': newest, 'operation-card--selected': selected }"
  >
    <div class="d-flex flex-row justify-space-between align-center flex-wrap flex-gap-2">
      <div class="d-flex flex-row align-center flex-gap-2">
        <v-checkbox
          class="ma-0 pa-0"
          dense
          hide-details
          :disabled="operation.length < 1"
          :input-value="selected"
          @change="$emit('update:selected', $event)"
        />
        <span class="operation-card__title">{{ details.alias }}</span>
        <copyable-id class="operation-card__subtle text-caption" :id="operation.id" type="Operation" />
        <v-chip outlined x-small :color="operation.amlCheck ? '#00c853' : '#ff6d00'">
          <v-icon class="mr-1" :color="operation.amlCheck ? '#00c853' : '#ff6d00'" x-small>
            {{ operation.amlCheck ? 'mdi-check' : 'mdi-alert' }}
          </v-icon>
          <span
            class="operation-card__aml-label"
            :style="{ color: operation.amlCheck ? '#00c853' : '#ff6d00' }"
          >
            {{ operation.amlCheck ? $t('instructions.amlApproved') : $t('instructions.amlCheck') }}
          </span>
        </v-chip>
      </div>

      <div class="d-flex flex-row align-center operation-card__compact-gap flex-wrap">
        <v-chip color="black" class="black-chip" x-small outlined>
          {{ details.type || '-' }}
        </v-chip>
        <span class="operation-card__subtle text-caption">
          <v-icon x-small class="mx-1">mdi-file-outline</v-icon>{{ details.docs }}
        </span>
        <!-- add more files (only for operations not yet linked to an RFQ) -->
        <template v-if="!operation.rfqId">
          <v-btn @click.stop="$emit('add-files', { operation })" outlined x-small :disabled="operation.length === 4">
            <v-icon x-small>mdi-upload</v-icon>
          </v-btn>
        </template>
        <v-btn icon x-small color="red" :disabled="deleteDisabled" @click="$emit('delete', operation.id)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="operation.rfqId" class="d-flex flex-row align-center justify-space-between flex-wrap mt-1">
      <copyable-id class="text-caption operation-card__subtle" :id="operation.rfqId" type="RFQ" />
      <div class="d-flex flex-row align-center flex-gap-4 flex-wrap">
        <div class="d-flex align-center text-caption font-weight-bold">
          <span
            class="mx-1"
            :style="details.side === 'BUY' ? 'color:#00c853' : 'color:#ff6d00'"
          >{{ details.side }}</span>
          <span class="mx-1">|</span>
          <v-chip outlined x-small>{{ details.status }}</v-chip>
        </div>
        <div class="d-flex flex-row align-center flex-no-wrap">
          <h6 class="py-1">{{ details.ccy }}</h6>
          <h4 class="font-weight-bold">&nbsp;{{ details.qty }}</h4>
        </div>
        <div v-if="details.settlement" class="operation-card__subtle text-caption">
          {{ $t('quoteInfo.settlement') }}: {{ details.settlement }}
        </div>
      </div>
    </div>

    <div
      v-if="operation.amlCheck && !operation.rfqId"
      class="d-flex flex-column flex-sm-row align-sm-center justify-space-between flex-gap-2 mt-1 pa-2 operation-card__approved"
    >
      <div class="text-caption">{{ $t('instructions.amlApprovedBannerMessage') }}</div>
      <div class="d-flex flex-wrap flex-gap-2">
        <!-- The tour opens its steps; a click on the button a step points at does not. -->
        <v-menu
          v-model="showTourStep1"
          :open-on-click="false"
          :close-on-content-click="false"
          :close-on-click="false"
          :z-index="9999"
          bottom
          offset-y
          content-class="rfq-tour-popover"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              x-small
              outlined
              data-testid="standard-rfq"
              v-bind="attrs"
              v-on="on"
              @click="$emit('standard-rfq', operation)"
            >
              {{ $t('broker.buttons.standardRfq') }}
            </v-btn>
          </template>
          <v-card class="pa-3" max-width="260" elevation="6" data-testid="rfq-tour-standard">
            <div class="font-weight-bold text-subtitle-2 mb-1">
              {{ $t('rfqTour.step1Title') }}
            </div>
            <div class="text-caption  text--darken-2 mb-1">
              {{ $t('rfqTour.step1Body') }}
            </div>
            <div class="d-flex flex-gap-2 justify-end">
              <v-btn
                x-small
                text
                class="pa-3 elevation-0"
                data-testid="rfq-tour-skip"
                @click.stop="finishTour"
              >
                {{ $t('rfqTour.skipTour') || 'Skip tour' }}
              </v-btn>
              <v-btn
                x-small
                color="primary"
                class="pa-3 elevation-0"
                data-testid="rfq-tour-next"
                @click.stop="nextTourStep"
              >
                {{ $t('instructions.next') || 'Next' }}
              </v-btn>
            </div>
          </v-card>
        </v-menu>

        <v-menu
          v-model="showTourStep2"
          :open-on-click="false"
          :close-on-content-click="false"
          :close-on-click="false"
          bottom
          offset-y
          :z-index="9999"
          content-class="rfq-tour-popover"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              x-small
              color="primary"
              data-testid="quick-rfq"
              v-bind="attrs"
              v-on="on"
              @click="$emit('quick-rfq', operation)"
            >
              {{ $t('broker.liveRates.quickRFQ') }}
            </v-btn>
          </template>
          <v-card class="pa-3" max-width="260" elevation="6" data-testid="rfq-tour-quick">
            <div class="font-weight-bold text-subtitle-2 mb-1">
              {{ $t('rfqTour.step2Title') }}
            </div>
            <div class="text-caption  text--darken-2 mb-3">
              {{ $t('rfqTour.step2Body') }}
            </div>
            <div class="d-flex justify-end">
              <v-btn
                x-small
                color="primary"
                data-testid="rfq-tour-finish"
                @click.stop="finishTour"
              >
                {{ $t('instructions.ok') || 'OK' }}
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>
  </v-card>
</template>

<script>
import CopyableId from '@/components/common/CopyableId.vue'

export default {
  name: 'OperationCard',
  components: { CopyableId },
  props: {
    operation: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    newest: {
      type: Boolean,
      default: false
    },
    showTour: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      tourStep: 1
    }
  },
  computed: {
    isTourCompleted () {
      const userId = localStorage.getItem('userId') || 'default'
      return localStorage.getItem(`pro-rfq-tour-completed:${userId}`) === 'true'
    },
    showTourStep1: {
      get () {
        return Boolean(this.showTour && !this.isTourCompleted && this.tourStep === 1)
      },
      set (val) {
        if (!val && this.tourStep === 1) {
          // tour step managed internally via nextTourStep
        }
      }
    },
    showTourStep2: {
      get () {
        return Boolean(this.showTour && !this.isTourCompleted && this.tourStep === 2)
      },
      set (val) {
        if (!val && this.tourStep === 2) {
          // tour step managed internally via finishTour
        }
      }
    },
    details () {
      const operation = this.operation
      const settlement = operation.settlement || operation.spotsett || operation.rfq_spotsett

      return {
        alias: operation.alias,
        ccy: operation.ccy || '',
        side: operation.side || '',
        qty: operation.qty || '',
        status: operation.status || '',
        docs: operation.length || 0,
        type: operation.operationType
          ? (this.$t('broker.operationTypes')[operation.operationType] || operation.operationType)
          : '',
        settlement: settlement ? String(settlement).toUpperCase() : ''
      }
    },
    deleteDisabled () {
      return this.operation.rfqId !== null
    }
  },
  watch: {
    showTour (val) {
      if (val && !this.isTourCompleted) {
        this.tourStep = 1
      }
    }
  },
  methods: {
    nextTourStep () {
      this.tourStep = 2
    },
    finishTour () {
      this.tourStep = 0
      const userId = localStorage.getItem('userId') || 'default'
      localStorage.setItem(`pro-rfq-tour-completed:${userId}`, 'true')
      this.$emit('tour-completed')
    }
  }
}
</script>

<style scoped>
.operation-card {
  display: flex;
  flex-direction: column;
  gap: .5rem;
  margin: .3rem 1rem;
  background-color: #2b2b2b87;
}

.operation-card--newest {
  border: 1px solid #0073ff !important;
}

.operation-card--selected {
  background-color: #0073ff47 !important;
}

.operation-card__subtle {
  color: rgb(168, 168, 168);
}

.operation-card__title {
  font-weight: bold;
  font-size: .85rem;
}

.operation-card__compact-gap {
  gap: .25rem;
}

.operation-card__aml-label {
  font-size: .6rem !important;
}

.operation-card__approved {
  background-color: rgba(0, 200, 83, 0.08);
  border: 1px solid rgba(0, 200, 83, 0.35);
  border-radius: 6px;
}

::v-deep.v-chip.v-chip--outlined.v-chip.v-chip.black-chip {
  background: #1e1e1e !important;
  border: 1px solid #3d3d3d !important;
  color: white !important;
}
</style>
